import React from 'react';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { fg } from '@atlaskit/platform-feature-flags';

import { IssueReformatterIcon } from '../../../../icons/prompt/issueReformatter';
import { jiraIssueReformatterApiURL } from '../../../utils';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';

export type GetAdditionalProps = () => Partial<{
	issue_summary?: string;
	issue_type_label?: string;
	issue_key?: string;
	project_id?: string;
	linked_urls?: string[];
}>;

type Options = {
	getAdditionalProps: GetAdditionalProps;
};

const getIssueType = (issueType: string | undefined) => {
	switch (issueType) {
		case 'bug':
			return 'Bug';
		case 'story':
			return 'Story';
		case 'epic':
			return 'Epic';
		case 'task':
		default:
			return 'Task'; // Default to 'Task' if issueType is not recognized
	}
};

export const createImproveDescription = (options: Options): EditorPluginAIConfigItem => ({
	key: CONFIG_ITEM_KEYS.ADD_STRUCTURE,
	title: aiConfigItemMessages.improveDescription,
	description: aiConfigItemMessages.reformatWithTemplateDescription,
	icon: () => <IssueReformatterIcon />,
	...(fg('migrate_issue_reformatter_to_aiwc_endpoint')
		? {
				endpoint: jiraIssueReformatterApiURL,
			}
		: {}),
	experienceId: 'issue-reformatter',
	intentSchemaId: 'issue_reformatter_intent_schema.json',
	featureId: 'issue_reformatter',
	alwaysNeedDoc: true,
	getRequestPayload: ({ locale, doc }) => {
		if (fg('migrate_issue_reformatter_to_aiwc_endpoint')) {
			// additional props come from Issue View
			const { issue_summary, project_id, issue_type_label, linked_urls } =
				options.getAdditionalProps?.();

			// all context is required except linked_urls
			const context = {
				project_id,
				summary: issue_summary,
				description: doc,
				...({ links: linked_urls } ?? []),
			};
			const issue_type = getIssueType(issue_type_label);

			// see https://hello.atlassian.net/wiki/spaces/AIDO/pages/5306581134/PWDC+Issue+Reformatter+API+Contracts
			const ai_feature_input = {
				source: 'JIRA',
				locale,
				context,
				response_type: 'NO_SCHEMA',
				suggested_issues_config: {
					use_template: true,
					max_issues: 1,
					suggested_issue_field_types: [
						{
							issue_type,
							fields: [{ field_name: 'Description', field_type: 'Paragraph' }],
						},
					],
				},
			};
			return { ai_feature_input };
		}

		return {
			recipient_agent_named_id: 'jira_issue_reformatter_agent',
			agent_input_context: {
				locale,
				issue_description: doc,
				...options.getAdditionalProps?.(),
			},
		};
	},
	actions: {
		empty: [
			createInsertBelow({ appearance: 'secondary' }),
			createReplace({ appearance: 'primary' }),
		],
		selection: [
			createInsertBelow({ appearance: 'secondary' }),
			createReplace({ appearance: 'primary' }),
		],
	},
	getBackendModel: () => 'assistance-service',
});

export const createImproveDescriptionWithOptions = (
	options: Options,
): EditorPluginAIConfigItemWithOptions => ({
	config: createImproveDescription(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
